import { useMemo, useEffect, useState } from "react";
import firstBy from "thenby";
import sortedUniqBy from "lodash/sortedUniqBy";
import { useQuery } from "@apollo/client";

import { GET_VISIBLE_CLIENTS } from "../queries/AdviserViewQueries";
import { setImpersonatedClient, stopImpersonating } from "../reducers/clientPortalUserActions";
import { checkResponseStatus } from "./ApiResponseHelper";
import { ENDPOINTS } from "../constants/routeConstants";

export const getSortedUniqueClients = locations =>
    sortedUniqBy(
        locations
            .flatMap(location => location.advisers)
            .flatMap(adviser => adviser.clients)
            .sort(firstBy("surname").thenBy("forename")),
        "clientId"
    );

export function useVisibleClients(selectedLocation, handleClientsQueryError = () => {}) {
    const {
        loading: clientsLoading,
        data = {},
        refetch: visibleClientsRefetch,
        error: visibleClientsError
    } = useQuery(GET_VISIBLE_CLIENTS, {
        variables: {
            locationId: selectedLocation?.locationId
        },
        skip: !selectedLocation?.locationId,
        onError: () => handleClientsQueryError(),
        notifyOnNetworkStatusChange: true
    });

    const visibleClients = useMemo(
        () => (data?.visibleLocations?.length ? getSortedUniqueClients(data.visibleLocations) : []),
        [data.visibleLocations]
    );

    return { clientsLoading, data, visibleClients, visibleClientsRefetch, visibleClientsError };
}

export function useLocationSelection(visibleLocations = [], previousLocationId) {
    const [selectedLocation, setSelectedLocation] = useState(null);

    useEffect(() => {
        if (visibleLocations?.length === 1) {
            setSelectedLocation(visibleLocations[0]);
        }
    }, [visibleLocations]);

    useEffect(() => {
        if (previousLocationId) {
            const previousLocation =
                visibleLocations.find(location => location.locationId === previousLocationId) ||
                null;
            setSelectedLocation(previousLocation);
        }
    }, [previousLocationId, visibleLocations]);

    return { selectedLocation, setSelectedLocation };
}

export const impersonateClient =
    (clientId, baseUrl = "") =>
    dispatch =>
        fetch(`${baseUrl}${ENDPOINTS.IMPERSONATE_USER}/${clientId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
            credentials: "same-origin"
        })
            .then(checkResponseStatus(`${baseUrl}${ENDPOINTS.IMPERSONATE_USER}/${clientId}`))
            .then(res => res.json())
            .then(client => {
                if (client) {
                    dispatch(setImpersonatedClient(client));
                }
            });

export const stopImpersonatingClient =
    (baseUrl = "") =>
    dispatch =>
        fetch(`${baseUrl}${ENDPOINTS.STOP_IMPERSONATING_CLIENT}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
            credentials: "same-origin"
        })
            .then(checkResponseStatus(`${baseUrl}${ENDPOINTS.STOP_IMPERSONATING_CLIENT}`))
            .then(() => dispatch(stopImpersonating()));
